import React, { useState, useEffect } from 'react';
import { collection, getDocs, setDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { Editor } from '@tinymce/tinymce-react';

interface NoticeAttachment {
  id: string;
  name: string;
  storagePath: string;
  size: number;
  contentType: string;
  uploadedAt: Timestamp;
}

interface Notice {
  id: string;
  title: string;
  content: string;
  isSecret?: boolean;
  attachments?: NoticeAttachment[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
}

const ALLOWED_ATTACHMENT_EXTENSIONS = [
  'pdf', 'hwp', 'hwpx', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'zip',
  'png', 'jpg', 'jpeg', 'gif', 'webp'
];
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const MAX_ATTACHMENTS_COUNT = 5;

const NoticeManagePage: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSecret, setIsSecret] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState<NoticeAttachment[]>([]);
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<NoticeAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState('');
  const { user, loading: authLoading } = useAdminAuth();

  useEffect(() => {
    if (!authLoading && user) {
      fetchNotices();
    }
  }, [user, authLoading]);

  const fetchNotices = async () => {
    try {
      const q = query(collection(db, 'notices'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const noticesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        attachments: Array.isArray(doc.data().attachments) ? doc.data().attachments : []
      } as Notice));
      setNotices(noticesData);
    } catch (error) {
      console.error('공지사항을 불러오는데 실패했습니다:', error);
    }
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setIsSecret(false);
    setExistingAttachments([]);
    setNewAttachments([]);
    setAttachmentsToDelete([]);
    setAttachmentError('');
    setEditingNotice(null);
  };

  const closeModal = () => {
    resetForm();
    setIsModalOpen(false);
  };

  const sanitizeFileName = (fileName: string) =>
    fileName.replace(/[^a-zA-Z0-9._-]/g, '_');

  const getFileExtension = (fileName: string) =>
    fileName.split('.').pop()?.toLowerCase() || '';

  const validateAttachmentFile = (file: File) => {
    const extension = getFileExtension(file.name);

    if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension)) {
      return '허용되지 않는 첨부파일 형식이 포함되어 있습니다.';
    }

    if (file.size > MAX_ATTACHMENT_SIZE) {
      return '첨부파일은 10MB 이하만 업로드할 수 있습니다.';
    }

    return '';
  };

  const uploadAttachment = async (noticeId: string, file: File): Promise<NoticeAttachment> => {
    const safeName = sanitizeFileName(file.name);
    const attachmentId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const storagePath = `notices/${noticeId}/attachments/${attachmentId}_${safeName}`;
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, file);

    return {
      id: attachmentId,
      name: file.name,
      storagePath,
      size: file.size,
      contentType: file.type || 'application/octet-stream',
      uploadedAt: Timestamp.now()
    };
  };

  const deleteAttachmentFile = async (attachment: NoticeAttachment) => {
    try {
      await deleteObject(ref(storage, attachment.storagePath));
    } catch (error) {
      console.error('첨부파일 삭제 실패:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || isSubmitting) return;

    const activeExistingAttachments = existingAttachments.filter(
      attachment => !attachmentsToDelete.some(target => target.id === attachment.id)
    );

    if (activeExistingAttachments.length + newAttachments.length > MAX_ATTACHMENTS_COUNT) {
      setAttachmentError(`첨부파일은 최대 ${MAX_ATTACHMENTS_COUNT}개까지 등록할 수 있습니다.`);
      return;
    }

    try {
      setIsSubmitting(true);
      if (editingNotice) {
        const uploadedAttachments = await Promise.all(
          newAttachments.map(file => uploadAttachment(editingNotice.id, file))
        );

        await updateDoc(doc(db, 'notices', editingNotice.id), {
          title,
          content,
          isSecret,
          attachments: [...activeExistingAttachments, ...uploadedAttachments],
          updatedAt: Timestamp.now(),
        });

        await Promise.all(attachmentsToDelete.map(deleteAttachmentFile));
      } else {
        const noticeRef = doc(collection(db, 'notices'));
        const uploadedAttachments = await Promise.all(
          newAttachments.map(file => uploadAttachment(noticeRef.id, file))
        );

        await setDoc(noticeRef, {
          title,
          content,
          isSecret,
          attachments: uploadedAttachments,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          createdBy: user.uid
        });
      }

      closeModal();
      fetchNotices();
    } catch (error) {
      console.error('공지사항 저장에 실패했습니다:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (notice: Notice) => {
    setEditingNotice(notice);
    setTitle(notice.title);
    setContent(notice.content);
    setIsSecret(notice.isSecret === true);
    setExistingAttachments(Array.isArray(notice.attachments) ? notice.attachments : []);
    setNewAttachments([]);
    setAttachmentsToDelete([]);
    setAttachmentError('');
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('정말로 이 공지사항을 삭제하시겠습니까?')) {
      try {
        const targetNotice = notices.find(notice => notice.id === id);
        const attachments = Array.isArray(targetNotice?.attachments) ? targetNotice!.attachments! : [];

        await Promise.all(attachments.map(deleteAttachmentFile));
        await deleteDoc(doc(db, 'notices', id));
        fetchNotices();
      } catch (error) {
        console.error('공지사항 삭제에 실패했습니다:', error);
      }
    }
  };

  const handleEditorChange = (content: string) => {
    setContent(content);
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) {
      return;
    }

    const activeExistingAttachments = existingAttachments.filter(
      attachment => !attachmentsToDelete.some(target => target.id === attachment.id)
    );

    if (activeExistingAttachments.length + newAttachments.length + selectedFiles.length > MAX_ATTACHMENTS_COUNT) {
      setAttachmentError(`첨부파일은 최대 ${MAX_ATTACHMENTS_COUNT}개까지 등록할 수 있습니다.`);
      e.target.value = '';
      return;
    }

    for (const file of selectedFiles) {
      const validationError = validateAttachmentFile(file);
      if (validationError) {
        setAttachmentError(validationError);
        e.target.value = '';
        return;
      }
    }

    setAttachmentError('');
    setNewAttachments(prev => [...prev, ...selectedFiles]);
    e.target.value = '';
  };

  const handleRemoveNewAttachment = (targetIndex: number) => {
    setNewAttachments(prev => prev.filter((_, index) => index !== targetIndex));
  };

  const handleRemoveExistingAttachment = (attachment: NoticeAttachment) => {
    setAttachmentsToDelete(prev => (
      prev.some(target => target.id === attachment.id)
        ? prev
        : [...prev, attachment]
    ));
  };

  const handleRestoreExistingAttachment = (attachment: NoticeAttachment) => {
    setAttachmentsToDelete(prev => prev.filter(target => target.id !== attachment.id));
  };

  const handleImageUpload = async (blobInfo: any): Promise<string> => {
    try {
      const file = blobInfo.blob();
      const fileName = `notice_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `notices/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      throw new Error('이미지 업로드에 실패했습니다.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">공지사항 관리</h1>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          새 공지사항 작성
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">제목</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">공개 여부</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">첨부파일</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작성일</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">수정일</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">관리</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {notices.map((notice) => (
              <tr key={notice.id}>
                <td className="px-6 py-4 whitespace-nowrap">{notice.title}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {notice.isSecret === true ? (
                    <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                      비밀
                    </span>
                  ) : (
                    <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                      공개
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {Array.isArray(notice.attachments) ? notice.attachments.length : 0}개
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {notice.createdAt.toDate().toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {notice.updatedAt.toDate().toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleEdit(notice)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => handleDelete(notice.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-4xl">
            <h2 className="text-xl font-bold mb-4">
              {editingNotice ? '공지사항 수정' : '새 공지사항 작성'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  제목
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  required
                />
              </div>
              <div className="mb-4 flex items-center">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSecret}
                    onChange={(e) => setIsSecret(e.target.checked)}
                    className="mr-2 h-4 w-4"
                  />
                  <span className="text-gray-700 text-sm font-bold">비밀글</span>
                </label>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  내용
                </label>
                <Editor
                  apiKey="3wrnkfcy50nmyc0b1h8co6u7hwjxbxbau9v8x9hmbmk8xbut"
                  value={content}
                  onEditorChange={handleEditorChange}
                  init={{
                    height: 500,
                    menubar: true,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                    ],
                    toolbar: 'undo redo | blocks | ' +
                      'bold italic forecolor | alignleft aligncenter ' +
                      'alignright alignjustify | bullist numlist outdent indent | ' +
                      'image | removeformat | help',
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                    images_upload_handler: handleImageUpload,
                    automatic_uploads: true,
                    file_picker_types: 'image',
                    images_reuse_filename: true,
                    images_upload_base_path: '/notices/',
                    images_upload_credentials: true,
                  }}
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  첨부파일
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  허용 형식: PDF, HWP, HWPX, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, ZIP, PNG, JPG, JPEG, GIF, WEBP / 파일당 10MB / 최대 5개
                </p>
                <input
                  type="file"
                  multiple
                  onChange={handleAttachmentChange}
                  className="block w-full text-sm text-gray-700 file:mr-4 file:rounded file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-semibold"
                />
                {attachmentError && (
                  <p className="mt-2 text-sm text-red-600">{attachmentError}</p>
                )}

                {existingAttachments.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">기존 첨부파일</p>
                    <ul className="space-y-2">
                      {existingAttachments.map((attachment) => {
                        const isMarkedForDeletion = attachmentsToDelete.some(target => target.id === attachment.id);

                        return (
                          <li
                            key={attachment.id}
                            className={`flex items-center justify-between rounded border px-3 py-2 text-sm ${
                              isMarkedForDeletion ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-gray-50 text-gray-700'
                            }`}
                          >
                            <span className="truncate pr-4">
                              {attachment.name} ({Math.ceil(attachment.size / 1024)}KB)
                            </span>
                            <button
                              type="button"
                              onClick={() => (
                                isMarkedForDeletion
                                  ? handleRestoreExistingAttachment(attachment)
                                  : handleRemoveExistingAttachment(attachment)
                              )}
                              className={isMarkedForDeletion ? 'text-blue-600 hover:text-blue-800' : 'text-red-600 hover:text-red-800'}
                            >
                              {isMarkedForDeletion ? '복원' : '삭제'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {newAttachments.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">새 첨부파일</p>
                    <ul className="space-y-2">
                      {newAttachments.map((file, index) => (
                        <li
                          key={`${file.name}-${file.size}-${index}`}
                          className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                        >
                          <span className="truncate pr-4">
                            {file.name} ({Math.ceil(file.size / 1024)}KB)
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveNewAttachment(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            삭제
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={closeModal}
                  className="bg-gray-500 text-white px-4 py-2 rounded mr-2 hover:bg-gray-600 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-blue-300"
                >
                  {isSubmitting ? '저장 중...' : editingNotice ? '수정' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoticeManagePage; 
